import { LevelName, LevelNameValues, Level, LevelStage, LootStage } from './level';
import { RoadDetails } from '../themes/theme';
import { level1 } from './level1';
import { level2 } from './level2';
import { level3 } from './level3';
import { level4 } from './level4';
import { level5 } from './level5';

export class LevelUtil {
  protected static roadMiddle: number = 0;
  protected static levelNameToLevelLookup: Record<LevelNameValues, Level> = {
    [LevelName.Level1]: level1,
    [LevelName.Level2]: level2,
    [LevelName.Level3]: level3,
    [LevelName.Level4]: level4,
    [LevelName.Level5]: level5
  };

  protected static nextLevelLookup: Record<LevelNameValues, LevelNameValues | undefined> = {
    [LevelName.Level1]: LevelName.Level2,
    [LevelName.Level2]: LevelName.Level3,
    [LevelName.Level3]: LevelName.Level4,
    [LevelName.Level4]: LevelName.Level5,
    [LevelName.Level5]: undefined
  };

  public static laneBoundariesByLane: { [key: number]: number } = {};
  public static laneBoundaries: number[] = [];

  public static getLevelFromName(levelName: LevelNameValues) {
    return this.levelNameToLevelLookup[levelName];
  }

  public static generateMeasurements(roadDetails: RoadDetails) {
    LevelUtil.roadMiddle = this.calculateRoadYMid(roadDetails);
    this.calculateLaneBoundaries(roadDetails);
  }

  public static get RoadMiddle() {
    return LevelUtil.roadMiddle;
  }

  public static getLaneYFromTopToTop(laneNumber: number, roadDetails: RoadDetails) {
    let scale = this.getLaneYScale(laneNumber, roadDetails);
    return roadDetails.laneHeight * scale;
  }

  public static getLaneYChangeMidToMid(prevLaneNumber: number, laneNumber: number, roadDetails: RoadDetails) {
    let preLane = this.getLaneYFromTopToTop(prevLaneNumber, roadDetails);
    let curLane = this.getLaneYFromTopToTop(laneNumber, roadDetails);

    return preLane + curLane * 0.5 - preLane * 0.5;
  }

  // public static getLaneY(laneNumber: number, roadDetails: RoadDetails) {
  //   return roadDetails.startPointY + roadDetails.laneHeight * laneNumber + roadDetails.laneHeight * 0.5;
  // }

  public static getYFromTopToLaneBot(laneNumber: number, roadDetails: RoadDetails) {
    let laneHeight = -50;
    for (let i = 0; i <= laneNumber; i++) {
      let scale = this.getLaneYScale(i, roadDetails);
      laneHeight += roadDetails.laneHeight * scale;
    }

    // console.log('Lane Y Height', laneHeight);
    return roadDetails.startPointY + laneHeight;
  }

  public static getLaneYFromTopToMid(laneNumber: number, roadDetails: RoadDetails) {
    let laneHeight = 0;
    for (let i = 0; i <= laneNumber; i++) {
      let scale = this.getLaneYScale(i, roadDetails);

      if (i == laneNumber) laneHeight += roadDetails.laneHeight * scale * 0.5;
      else laneHeight += roadDetails.laneHeight * scale;
    }

    // console.log('Lane Y Mid Height', roadDetails.startPointY, laneHeight, roadDetails.startPointY + laneHeight);
    return roadDetails.startPointY + laneHeight;
  }

  // public static getLaneYMiddle(laneNumber: number, roadDetails: RoadDetails) {
  //   return roadDetails.startPointY + roadDetails.laneHeight * laneNumber + roadDetails.laneHeight * 0.5;
  // }

  public static getIsAtTopOfRoad(y: number, roadDetails: RoadDetails): boolean {
    return y <= roadDetails.roadStartY;
  }

  public static getIsAtBottomOfRoad(y: number, roadDetails: RoadDetails): boolean {
    return y >= roadDetails.roadEndY;
  }

  public static calculateRoadYMid(roadDetails: RoadDetails) {
    // return (
    //   roadDetails.startPointY +
    //   (this.getLaneYFromTopToTop(0, roadDetails) +
    //     this.getLaneYFromTopToTop(1, roadDetails) +
    //     this.getLaneYFromTopToTop(2, roadDetails) +
    //     this.getLaneYFromTopToTop(3, roadDetails)) *
    //     0.5
    // );
    // return roadDetails.startPointY + 185.5;

    return roadDetails.roadStartY + (roadDetails.roadEndY - roadDetails.roadStartY) * 0.5;
  }

  public static calculateLaneBoundaries(roadDetails: RoadDetails) {
    let laneY = roadDetails.startPointY;

    for (let i = 0; i < roadDetails.laneCount; i++) {
      this.laneBoundariesByLane[i] = laneY;
      this.laneBoundaries.push(laneY);

      let scale = this.getLaneYScale(i, roadDetails);
      laneY += roadDetails.laneHeight * scale;
    }

    this.laneBoundariesByLane[roadDetails.laneCount] = laneY;
    this.laneBoundaries.push(laneY);
  }

  public static getLaneYScale(laneNumber: number, roadDetails: RoadDetails) {
    return 1 - (roadDetails.laneCount - laneNumber - 1) * roadDetails.laneScale;
  }

  public static getLaneYVehicleScale(laneNumber: number, roadDetails: RoadDetails) {
    return 1 - (roadDetails.laneCount - laneNumber) * roadDetails.laneVehicleScale;
  }

  public static getPavementY(roadDetails: RoadDetails) {
    return roadDetails.startPointY + roadDetails.pavementY;
  }

  public static getPavementYMid(roadDetails: RoadDetails) {
    return roadDetails.startPointY + roadDetails.pavementY + roadDetails.pavementHeight * 0.5;
  }

  public static getVehicleLaneYFromTopToMid(laneNumber: number, roadDetails: RoadDetails) {
    // console.log('Vehicle Lane Y Mid Height', this.getYFromTopToLaneMiddle(laneNumber, roadDetails) + roadDetails.vehicleOffset);
    return this.getLaneYFromTopToMid(laneNumber, roadDetails) + roadDetails.vehicleOffset;
  }

  public static getVehicleLaneY(laneNumber: number, roadDetails: RoadDetails) {
    let scale = this.getLaneYScale(laneNumber, roadDetails);
    return roadDetails.laneHeight * scale;
  }

  public static getVehicleStartPoint(laneNumber: number, startIndentX: number, roadDetails: RoadDetails): [x: number, y: number] {
    let startPointX = roadDetails.startPointX + startIndentX;
    // let startPointY = roadDetails.startPointY + roadDetails.laneHeight * laneNumber + roadDetails.laneHeight * 0.5;
    let startPointY = this.getVehicleLaneYFromTopToMid(laneNumber, roadDetails);

    return [startPointX, startPointY];
  }

  public static calculateStarRating(level: Level, earnedScore: number): number {
    const moneyValue = 1;
    const bagValue = 5;
    const presentValue = 10;

    let totalMoney = 0;

    for (const stage of level.stages) {
      if (stage.stageType == LevelStage.LootDropSection) {
        let lootDropStage = stage as LootStage;

        let bagDrops = Math.floor(lootDropStage.dropQuantity / lootDropStage.specialDropInterval);
        totalMoney += lootDropStage.dropQuantity - bagDrops;
        totalMoney += bagDrops * bagValue;
      } else if (stage.stageType == LevelStage.RampSection) {
        totalMoney += presentValue;
      }
    }

    let percent = earnedScore / totalMoney;
    let stars = 0;

    for (const ratingRequired of level.scoring.starRatings) {
      if (percent >= ratingRequired) stars++;
    }

    console.log('Calculated Score', totalMoney, earnedScore, stars);

    return stars;
  }

  public static getNextLevel(previousLevel: LevelNameValues): LevelNameValues | undefined {
    let nextLevel = LevelUtil.nextLevelLookup[previousLevel];
    return nextLevel;
  }
}
